import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { getApiErrorMessage } from '@/api/apiErrors';
import { passwordResetRequest } from '@/api/apiAuth';
import { InputForm } from '@/components/CustomForm/components';
import { Button } from '@/components/common/Button';
import { AppModal } from '@/components/Modal/AppModal';

import './password-reset-modal.scss';

type PasswordResetValues = {
  email: string;
};

type PasswordResetModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function PasswordResetModal({ open, onOpenChange }: PasswordResetModalProps) {
  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<PasswordResetValues>({
    defaultValues: {
      email: '',
    },
    mode: 'onChange',
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset({ email: '' });
      setIsSending(false);
    }

    onOpenChange(nextOpen);
  };

  const footer = (
    <div className="form-actions">
      <Button type="button" variant="app" tone="secondary" onClick={() => handleOpenChange(false)}>
        {t('users.resetDialog.cancel')}
      </Button>
      <Button type="submit" variant="app" tone="primary" form="password-reset-form" disabled={isSending || !isValid}>
        {isSending ? t('users.resetDialog.sending') : t('users.resetDialog.submit')}
      </Button>
    </div>
  );

  return (
    <AppModal
      open={open}
      onOpenChange={handleOpenChange}
      title={t('users.resetDialog.title')}
      description={t('users.resetDialog.description')}
      size="compact"
      footer={footer}
    >
      <form
        id="password-reset-form"
        className="stack password-reset-dialog__form"
        onSubmit={handleSubmit(async (values) => {
          setIsSending(true);
          try {
            await passwordResetRequest({ email: values.email });
            toast.success(t('users.resetDialog.successBody'));
            handleOpenChange(false);
          } catch (error) {
            toast.error(getApiErrorMessage(error, t('users.messages.passwordResetError')));
          } finally {
            setIsSending(false);
          }
        })}
      >
        <InputForm
          label={t('users.resetDialog.emailLabel')}
          type="email"
          placeholder={t('users.resetDialog.emailPlaceholder')}
          autoComplete="email"
          error={errors.email?.message}
          {...register('email', {
            required: t('users.resetDialog.emailRequired'),
          })}
        />
      </form>
    </AppModal>
  );
}